import classNames from "classnames";
import React, { FC } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { LuSearch } from "react-icons/lu";

import { Avatar } from "../avatar";
import { Button } from "../button";
import { FitChips } from "../chips";
import { Input } from "../input";
import {
  SelectEmptyStateConfig,
  SelectFooterConfig,
  SelectOption,
  SelectProps,
  SelectSearchConfig,
} from "./Select.types";

export const SelectSpinner: FC<{ className?: string }> = ({ className }) => (
  <AiOutlineLoading3Quarters
    className={classNames("animate-spin text-[#6B7185]", className)}
    size={16}
  />
);

type SelectSearchProps = {
  search: Required<Pick<SelectSearchConfig, "placeholder" | "mode">> &
    SelectSearchConfig;
  value: string;
  onChange: (value: string) => void;
  embedded?: boolean;
};

export const SelectSearch: FC<SelectSearchProps> = ({
  search,
  value,
  onChange,
  embedded,
}) => (
  <div
    className={
      embedded ? "mb-3 border-0 p-0" : "border-b border-[#EEF0F3] p-2"
    }>
    <Input
      size={embedded ? "md" : "sm"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={search.placeholder}
      leftIcon={<LuSearch size={16} />}
      rightIcon={search.loading ? <SelectSpinner /> : undefined}
      className={classNames(
        "text-sm",
        embedded ? "h-[43px] rounded-[9px] border-[1.5px]" : "h-9 min-h-0",
      )}
    />
  </div>
);

type OptionContentProps = {
  option: SelectOption;
};

export const OptionContent: FC<OptionContentProps> = ({ option }) => (
  <>
    {option.avatar && typeof option.avatar === "object" && (
      <Avatar {...option.avatar} size={option.avatar.size ?? "sm"} />
    )}
    <span className={"min-w-0 flex-1"}>
      <span
        className={classNames(
          "block truncate text-sm text-[#3A4256]",
          option.isBold ? "font-bold" : "font-semibold",
        )}>
        {option.label}
      </span>
      {option.caption && (
        <span className={"block truncate text-[11.5px] text-[#9097A8]"}>
          {option.caption}
        </span>
      )}
    </span>
    {(option.meta || typeof option.totalCount === "number") && (
      <span className={"shrink-0 text-xs font-medium text-[#9097A8]"}>
        {option.meta ?? option.totalCount}
      </span>
    )}
  </>
);

type SelectEmptyStateProps = {
  emptyLabel: string;
  emptyState?: SelectEmptyStateConfig;
};

export const SelectEmptyState: FC<SelectEmptyStateProps> = ({
  emptyLabel,
  emptyState,
}) => {
  if (!emptyState) {
    return (
      <span className={"text-sm font-medium text-[#9097A8]"}>{emptyLabel}</span>
    );
  }

  return (
    <span
      className={
        "flex flex-col items-center gap-[3px] text-center font-Manrope"
      }>
      {emptyState.icon && (
        <span
          className={
            "mb-1 inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] bg-[#F0EDFF] text-[#6B4EFF]"
          }>
          {emptyState.icon}
        </span>
      )}
      {emptyState.title && (
        <span className={"text-[13px] font-bold text-[#1F2433]"}>
          {emptyState.title}
        </span>
      )}
      {emptyState.description && (
        <span className={"max-w-[230px] text-xs leading-[1.4] text-[#9097A8]"}>
          {emptyState.description}
        </span>
      )}
    </span>
  );
};

type SelectValueViewProps = {
  selectedOptions: SelectOption[];
  isMulti: boolean;
  placeholder?: string;
  renderValue?: SelectProps["renderValue"];
  onRemove: (option: SelectOption) => void;
};

export const SelectValueView: FC<SelectValueViewProps> = ({
  selectedOptions,
  isMulti,
  placeholder,
  renderValue,
  onRemove,
}) => {
  if (selectedOptions.length === 0) {
    return <span className={"truncate text-[#9097A8]"}>{placeholder}</span>;
  }

  const renderedValue = renderValue?.(
    isMulti ? selectedOptions : selectedOptions[0],
  );

  if (renderedValue) {
    return <>{renderedValue}</>;
  }

  if (!isMulti) {
    return (
      <span className={"truncate font-medium text-[#1F2433]"}>
        {selectedOptions[0].label}
      </span>
    );
  }

  return (
    <FitChips
      items={selectedOptions.map((option) => ({
        key: String(option.value),
        label: String(option.label),
      }))}
      onRemove={(key) => {
        const option = selectedOptions.find(
          (selected) => String(selected.value) === key,
        );
        if (option) onRemove(option);
      }}
    />
  );
};

type SelectFooterProps = {
  footer?: SelectFooterConfig;
  selectedCount: number;
  clear: () => void;
  done: () => void;
  close: () => void;
};

export const SelectFooter: FC<SelectFooterProps> = ({
  footer,
  selectedCount,
  clear,
  done,
  close,
}) => {
  if (!footer) {
    return null;
  }

  if (footer.render) {
    return (
      <div className={"border-t border-[#EEF0F3] p-2"}>
        {footer.render({ selectedCount, clear, done, close })}
      </div>
    );
  }

  if (footer.variant === "selected-count") {
    return (
      <div
        className={
          "flex items-center justify-between border-t border-[#EEF0F3] px-3 py-2 text-xs font-semibold text-[#5B6170]"
        }>
        <span>
          {footer.selectedCountLabel
            ? footer.selectedCountLabel(selectedCount)
            : `${selectedCount} selected`}
        </span>
        <Button variant={"ghost"} size={"sm"} autoWidth onClick={done}>
          {footer.doneLabel ?? "Done"}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={
        "flex items-center justify-end gap-2 border-t border-[#EEF0F3] p-2"
      }>
      <Button variant={"outline"} size={"sm"} autoWidth onClick={clear}>
        {footer.clearLabel ?? "Clear"}
      </Button>
      <Button size={"sm"} autoWidth onClick={done}>
        {footer.doneLabel ?? "Done"}
      </Button>
    </div>
  );
};
